class TopRegionsCard {
	constructor(element) {
		this.element = element;
		this.summary = this.getSummary();

		this.listElement = element.querySelector(
			'[data-top-regions-list]'
		);

		this.yearButtons = element.querySelectorAll(
			'[data-year]'
		);

		this.bindEvents();

		/*
		 * Domyślnie pokazujemy dane zagregowane
		 * ze wszystkich dostępnych lat.
		 *
		 * Nie dodajemy przycisku „Wszystkie lata”.
		 */
		this.renderAllYears();
	}

	/**
	 * Pobiera dane zapisane w atrybucie data-summary.
	 *
	 * @return {Object}
	 */
	getSummary() {
		try {
			return JSON.parse(
				this.element.dataset.summary || '{}'
			);
		} catch (error) {
			console.error(
				'Nie udało się odczytać danych TOP województw:',
				error
			);

			return {};
		}
	}

	/**
	 * Sumuje dane województw ze wszystkich lat.
	 *
	 * @return {Object}
	 */
	getAggregatedYearData() {
		const aggregated = {};

		Object.values(this.summary).forEach(
			(yearData) => {
				const regions = Array.isArray(
					yearData.reportsByRegion
				)
					? yearData.reportsByRegion
					: [];

				regions.forEach((item) => {
					const region = String(
						item.region || ''
					).trim();

					const key = region.toLocaleLowerCase(
						'pl-PL'
					);

					if (!key) {
						return;
					}

					if (!aggregated[key]) {
						aggregated[key] = {
							region,
							count: 0
						};
					}

					aggregated[key].count += Number(
						item.count || 0
					);
				});
			}
		);

		const sortedRegions = Object.values(
			aggregated
		).sort(
			(firstRegion, secondRegion) =>
				secondRegion.count - firstRegion.count
		);

		return {
			topRegions: sortedRegions.slice(0, 3),
			reportsByRegion: sortedRegions
		};
	}

	/**
	 * Podłącza obsługę przycisków i synchronizację z mapą.
	 */
	bindEvents() {
		this.yearButtons.forEach((button) => {
			button.addEventListener(
				'click',
				() => {
					const year =
						button.dataset.year || '';

					if (!year) {
						return;
					}

					this.render(year);
				}
			);
		});

		/*
		 * Synchronizacja z filtrem roku na mapie.
		 *
		 * Konkretna wartość oznacza wybrany rok.
		 * null oznacza wszystkie lata.
		 */
		document.addEventListener(
			'stop18:year-change',
			(event) => {
				const year = event.detail?.year;

				if (!year) {
					this.renderAllYears();

					return;
				}

				this.render(
					String(year)
				);
			}
		);
	}

	/**
	 * Pokazuje dane ze wszystkich lat.
	 */
	renderAllYears() {
		this.clearActiveYear();

		this.renderFromData(
			this.getAggregatedYearData()
		);
	}

	/**
	 * Pokazuje dane dla wybranego roku.
	 *
	 * @param {string} year Wybrany rok.
	 */
	render(year) {
		if (!this.listElement) {
			return;
		}

		const yearData =
			this.summary[String(year)];

		if (!yearData) {
			/*
			 * Jeśli rok nie istnieje w danych,
			 * nie pokazujemy od razu komunikatu „Brak danych”.
			 * Zamiast tego wracamy do wszystkich lat.
			 */
			this.renderAllYears();

			return;
		}

		this.setActiveYear(
			String(year)
		);

		this.renderFromData(
			yearData
		);
	}

	/**
	 * Renderuje listę województw.
	 *
	 * @param {Object} yearData Dane roku lub dane zagregowane.
	 */
	renderFromData(yearData) {
		if (!this.listElement) {
			return;
		}

		const regions =
			this.getTopRegions(yearData);

		if (!regions.length) {
			this.listElement.innerHTML = `
				<p class="top-regions-card__empty">
					Brak danych.
				</p>
			`;

			return;
		}

		this.listElement.innerHTML = regions
			.map(
				(item) => `
					<div class="top-regions-card__item">
						<span class="top-regions-card__region">
							${this.escapeHtml(item.region)}
						</span>

						<strong class="top-regions-card__count">
							${Number(item.count || 0)}
						</strong>
					</div>
				`
			)
			.join('');
	}

	/**
	 * Ustawia aktywny przycisk roku.
	 *
	 * @param {string} year Wybrany rok.
	 */
	setActiveYear(year) {
		this.yearButtons.forEach((button) => {
			const isActive =
				button.dataset.year === String(year);

			button.classList.toggle(
				'is-active',
				isActive
			);

			button.setAttribute(
				'aria-pressed',
				String(isActive)
			);
		});
	}

	/**
	 * Usuwa aktywny stan z przycisków.
	 *
	 * Dane ze wszystkich lat są widoczne,
	 * ale żaden dodatkowy przycisk nie jest dodawany.
	 */
	clearActiveYear() {
		this.yearButtons.forEach((button) => {
			button.classList.remove(
				'is-active'
			);

			button.setAttribute(
				'aria-pressed',
				'false'
			);
		});
	}

	/**
	 * Pobiera trzy województwa z największą liczbą zgłoszeń.
	 *
	 * @param {Object} yearData Dane statystyk.
	 *
	 * @return {Array}
	 */
	getTopRegions(yearData) {
		if (
			Array.isArray(
				yearData.topRegions
			)
			&& yearData.topRegions.length
		) {
			return yearData.topRegions.slice(
				0,
				3
			);
		}

		if (
			!Array.isArray(
				yearData.reportsByRegion
			)
		) {
			return [];
		}

		return [
			...yearData.reportsByRegion
		]
			.sort(
				(firstRegion, secondRegion) =>
					Number(
						secondRegion.count || 0
					)
					- Number(
						firstRegion.count || 0
					)
			)
			.slice(0, 3);
	}

	/**
	 * Zabezpiecza tekst przed wstawieniem do HTML.
	 *
	 * @param {*} value Wartość do zabezpieczenia.
	 *
	 * @return {string}
	 */
	escapeHtml(value) {
		return String(value)
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#039;');
	}
}

/**
 * Inicjalizuje karty TOP województw.
 */
const initializeTopRegionsCards = () => {
	document
		.querySelectorAll(
			'.js-top-regions-card'
		)
		.forEach((element) => {
			if (
				element.dataset
					.topRegionsInitialized
				=== 'true'
			) {
				return;
			}

			element.dataset
				.topRegionsInitialized =
					'true';

			new TopRegionsCard(element);
		});
};

if (document.readyState === 'loading') {
	document.addEventListener(
		'DOMContentLoaded',
		initializeTopRegionsCards
	);
} else {
	initializeTopRegionsCards();
}

document.addEventListener(
	'elementor/frontend/init',
	initializeTopRegionsCards
);